import { UserRole } from '@comp-sys/shared';

/**
 * 引导式 UI 资格角色:参赛选手 / 教练 / 志愿者 / 裁判。
 * 管理员(SA/SRA/ADMIN/CM)与学校/机构(Phase 2)不在此列,永远走经典看板。
 */
export const GUIDED_ELIGIBLE_ROLES: UserRole[] = [
  UserRole.CONTESTANT,
  UserRole.COACH,
  UserRole.VOLUNTEER,
  UserRole.JUDGE,
];

export function isGuidedEligible(role: UserRole | undefined): boolean {
  return !!role && GUIDED_ELIGIBLE_ROLES.includes(role);
}

/**
 * 参与者引导状态机(见 spec §4)。
 * NO_PROFILE → PROFILE_PENDING → PROFILE_REJECTED →(APPROVED)→
 * READY_TO_ENROLL → ENROLLED → AWARDED
 */
export type ParticipantState =
  | 'NO_PROFILE'
  | 'PROFILE_PENDING'
  | 'PROFILE_REJECTED'
  | 'READY_TO_ENROLL'
  | 'ENROLLED'
  | 'AWARDED';

export interface ParticipantStateInput {
  /** 当前用户全部档案的 status(DRAFT/PENDING/APPROVED/REJECTED) */
  profileStatuses: string[];
  /** 是否存在 ACTIVE 报名 */
  hasActiveEnrollment: boolean;
  /** 是否已有奖项/证书 */
  hasAwards: boolean;
}

/** 档案状态优先级(用于"最佳"状态):APPROVED > PENDING > REJECTED > DRAFT */
const PROFILE_RANK: Record<string, number> = { APPROVED: 4, PENDING: 3, REJECTED: 2, DRAFT: 1 };

export function bestProfileStatus(statuses: string[]): string | null {
  if (!statuses.length) return null;
  return statuses.reduce<string | null>((best, s) => {
    if (best === null) return s;
    return (PROFILE_RANK[s] ?? 0) >= (PROFILE_RANK[best] ?? 0) ? s : best;
  }, null);
}

export function computeParticipantState(input: ParticipantStateInput): ParticipantState {
  const best = bestProfileStatus(input.profileStatuses);
  if (best !== 'APPROVED') {
    if (best === 'PENDING') return 'PROFILE_PENDING';
    if (best === 'REJECTED') return 'PROFILE_REJECTED';
    return 'NO_PROFILE';
  }
  // 已通过:活跃报名优先于已获奖(进行中的赛事更需要关注)
  if (input.hasActiveEnrollment) return 'ENROLLED';
  if (input.hasAwards) return 'AWARDED';
  return 'READY_TO_ENROLL';
}

/** 主卡"最紧急赛事"选择:有待办的优先,否则取第一个。 */
export interface UrgentEnrollment {
  teamId: string;
  hasTodo: boolean;
}

export function pickUrgentEnrollment(enrollments: UrgentEnrollment[]): string | null {
  if (!enrollments.length) return null;
  return (enrollments.find((e) => e.hasTodo) ?? enrollments[0]).teamId;
}
